import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `/api`;

const stages = ['Inquiry', 'Discovery', 'Proposal Sent', 'Negotiation', 'Won', 'Lost'];

const stageColors = {
  Inquiry: 'bg-blue-500/10 text-blue-600',
  Discovery: 'bg-yellow-500/10 text-yellow-600',
  'Proposal Sent': 'bg-purple-500/10 text-purple-600',
  Negotiation: 'bg-orange-500/10 text-orange-600',
  Won: 'bg-green-500/10 text-green-600',
  Lost: 'bg-red-500/10 text-red-600'
};

const Deals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('All');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    estimated_value: '',
    probability: 50,
    expected_close_date: '',
    stage: 'Inquiry',
    notes: ''
  });

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const res = await axios.get(`${API}/deals`);
      setDeals(res.data);
    } catch {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const pipelineValue = useMemo(() => {
    return deals
      .filter(d => !['Won', 'Lost'].includes(d.stage))
      .reduce((sum, d) => sum + (d.estimated_value || 0), 0);
  }, [deals]);

  const filteredDeals = useMemo(() => {
    return deals.filter(deal => {
      const matchesSearch =
        deal.title.toLowerCase().includes(search.toLowerCase());

      const matchesStage =
        stageFilter === 'All' || deal.stage === stageFilter;

      return matchesSearch && matchesStage;
    });
  }, [deals, search, stageFilter]);

  const handleStageChange = async (deal, newStage) => {
    try {
      await axios.put(`${API}/deals/${deal.id}`, {
        ...deal,
        stage: newStage
      });
      fetchDeals();
      toast.success('Stage updated');
    } catch {
      toast.error('Failed to update stage');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      estimated_value: parseFloat(formData.estimated_value),
      probability: parseInt(formData.probability)
    };

    try {
      if (editingDeal) {
        await axios.put(`${API}/deals/${editingDeal.id}`, payload);
        toast.success('Deal updated');
      } else {
        await axios.post(`${API}/deals`, payload);
        toast.success('Deal created');
      }

      handleCancel();
      fetchDeals();
    } catch {
      toast.error('Failed to save deal');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    await axios.delete(`${API}/deals/${id}`);
    fetchDeals();
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setFormData({
      ...deal,
      expected_close_date: deal.expected_close_date
        ? deal.expected_close_date.split('T')[0]
        : ''
    });
    setIsDialogOpen(true);
  };

  const handleCancel = () => {
    setEditingDeal(null);
    setFormData({
      title: '',
      estimated_value: '',
      probability: 50,
      expected_close_date: '',
      stage: 'Inquiry',
      notes: ''
    });
    setIsDialogOpen(false);
  };

  if (loading) {
    return <div className="text-center py-12">Loading deals...</div>;
  }

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="text-2xl font-semibold text-app-text">
          Pipeline Value: ₹{pipelineValue.toLocaleString('en-IN')}
        </div>

        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Deal
        </Button>
      </div>

      {/* FILTERS */}
      <div className="flex flex-col md:flex-row gap-4">
        <Input
          placeholder="Search deals..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:w-64"
        />

        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="md:w-48">
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All Stages</SelectItem>
            {stages.map(stage => (
              <SelectItem key={stage} value={stage}>
                {stage}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* TABLE */}
      <div className="bg-app-card border border-app-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-app-muted text-app-subtext">
            <tr>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Value</th>
              <th className="px-4 py-3 text-left">Probability</th>
              <th className="px-4 py-3 text-left">Stage</th>
              <th className="px-4 py-3 text-left">Close Date</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredDeals.map(deal => (
              <tr
                key={deal.id}
                className="border-t border-app-border hover:bg-app-muted/40"
              >
                <td className="px-4 py-3 font-medium text-app-text">
                  {deal.title}
                </td>

                <td className="px-4 py-3">
                  ₹{deal.estimated_value?.toLocaleString('en-IN')}
                </td>

                <td className="px-4 py-3">
                  {deal.probability}%
                </td>

                <td className="px-4 py-3">
                  <Select
                    value={deal.stage}
                    onValueChange={(val) => handleStageChange(deal, val)}
                  >
                    <SelectTrigger className={`h-8 px-2 text-xs ${stageColors[deal.stage]}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map(stage => (
                        <SelectItem key={stage} value={stage}>
                          {stage}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>

                <td className="px-4 py-3">
                  {deal.expected_close_date
                    ? new Date(deal.expected_close_date).toLocaleDateString()
                    : '-'}
                </td>

                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => handleEdit(deal)}>
                    <Edit className="w-4 h-4 text-blue-500 inline" />
                  </button>
                  <button onClick={() => handleDelete(deal.id)}>
                    <Trash2 className="w-4 h-4 text-red-500 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredDeals.length === 0 && (
          <div className="text-center py-8 text-app-subtext">
            No deals found.
          </div>
        )}
      </div>

      {/* DIALOG */}
      <Dialog open={isDialogOpen} onOpenChange={(o) => !o && handleCancel()}>
        <DialogContent className="bg-app-card border border-app-border">
          <DialogHeader>
            <DialogTitle>
              {editingDeal ? 'Edit Deal' : 'New Deal'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">

            <div>
              <Label>Deal Title *</Label>
              <Input
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Stage</Label>
              <Select
                value={formData.stage}
                onValueChange={(val) =>
                  setFormData({ ...formData, stage: val })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stages.map(stage => (
                    <SelectItem key={stage} value={stage}>
                      {stage}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Expected Close Date</Label>
              <Input
                type="date"
                value={formData.expected_close_date}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expected_close_date: e.target.value
                  })
                }
              />
            </div>

            <div>
              <Label>Estimated Value *</Label>
              <Input
                type="number"
                required
                value={formData.estimated_value}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    estimated_value: e.target.value
                  })
                }
              />
            </div>

            <div>
              <Label>Probability (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.probability}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    probability: e.target.value
                  })
                }
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    notes: e.target.value
                  })
                }
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">
                {editingDeal ? 'Update' : 'Create'}
              </Button>
            </div>

          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Deals;